package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.key
import androidx.compose.ui.layout.ContentScale
import coil3.compose.AsyncImage
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.data.AdminRepo
import org.gymly.model.AdminBillingPeriod
import org.gymly.model.AdminClient
import org.gymly.model.AdminMembershipOrderStatus
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.AdminPaymentDiscount
import org.gymly.model.AdminPersonSex
import org.gymly.model.AdminTrainer
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.AdminDateField
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.imagePreviewModel
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.todayIsoDate
import kotlin.time.Instant

@Composable
fun BulkClientPhotoPreview(
    client: AdminClient,
    modifier: Modifier = Modifier,
    size: androidx.compose.ui.unit.Dp = 108.dp,
) {
    var photoBytes by remember(client.id) { mutableStateOf<ByteArray?>(null) }
    LaunchedEffect(client.id, client.photoUrl) {
        photoBytes = if (!client.photoUrl.isNullOrBlank()) {
            runCatching { AdminRepo.api.fetchClientPhotoBytes(client.id) }.getOrNull()
        } else {
            null
        }
    }
    val previewModel = imagePreviewModel(photoBytes)
    val initials = "${client.firstName.firstOrNull() ?: '?'}${client.lastName.firstOrNull() ?: '?'}".uppercase()
    Box(
        modifier
            .size(size)
            .clip(CircleShape)
            .background(AdminColors.Outline.copy(alpha = 0.2f))
            .border(2.dp, if (previewModel != null) AdminColors.Primary else AdminColors.Outline, CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        if (previewModel != null) {
            key(previewModel.toString().takeLast(40)) {
                AsyncImage(
                    model = previewModel,
                    contentDescription = "Foto de ${client.fullName}",
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop,
                )
            }
        } else {
            Text(initials, fontWeight = FontWeight.Bold, fontSize = (size.value / 3).sp, color = AdminColors.Primary)
        }
    }
}

fun BulkMemberRow.withExistingClient(client: AdminClient): BulkMemberRow = copy(
    linkedClientId = client.id,
    firstName = client.firstName,
    lastName = client.lastName,
    email = client.email.orEmpty(),
    phone = client.phone.orEmpty(),
    docId = client.docId.orEmpty(),
    sex = client.sex,
    address = client.address.orEmpty(),
    result = null,
    ok = null,
)

fun BulkMemberRow.clearLinkedClient(): BulkMemberRow = copy(linkedClientId = null, result = null, ok = null)

/** Al editar a mano se suelta el vínculo con el socio existente. */
private fun BulkMemberRow.editPerson(clearLink: Boolean = linkedClientId != null, block: BulkMemberRow.() -> BulkMemberRow): BulkMemberRow {
    val next = block()
    return if (clearLink) next.copy(linkedClientId = null, result = null, ok = null) else next.copy(result = null, ok = null)
}

/**
 * Escritorio: [first] a la izquierda y [second] a la derecha (ocupa el resto).
 * Celular ([narrow]): [first] arriba y [second] debajo, a todo el ancho.
 */
@Composable
private fun BulkCardSplit(
    narrow: Boolean,
    first: @Composable () -> Unit,
    second: @Composable () -> Unit,
) {
    if (narrow) {
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(14.dp)) {
            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) { first() }
            Box(Modifier.fillMaxWidth()) { second() }
        }
    } else {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.Top,
        ) {
            Box { first() }
            Box(Modifier.weight(1f)) { second() }
        }
    }
}

@Composable
fun BulkEnrollMemberCard(
    index: Int,
    row: BulkMemberRow,
    plans: List<AdminMembershipPlanItem>,
    trainers: List<AdminTrainer>,
    existingClients: List<AdminClient> = emptyList(),
    excludeClientIds: Set<String> = emptySet(),
    /** Otras filas de la planilla (para detectar plan+periodo repetido en la misma carga). */
    siblingRows: List<BulkMemberRow> = emptyList(),
    enabled: Boolean,
    canRemove: Boolean,
    onRowChange: (BulkMemberRow) -> Unit,
    onUploadPhoto: () -> Unit,
    onCapturePhoto: () -> Unit,
    onClearPhoto: () -> Unit,
    onOpenPayment: () -> Unit,
    onClearPayment: () -> Unit,
    onRemove: () -> Unit,
) {
    val rowAmountDefault = plans.firstOrNull { it.id == row.planId }?.let { (it.priceCents / 100.0).toString() } ?: ""
    val amountCents = parseBulkAmountToCents(row.amountOverride.ifBlank { rowAmountDefault })
    val paymentSummary = paymentStatusLabel(
        configured = row.paymentConfigured,
        status = row.paymentStatus,
        lines = row.paymentLines,
        amountCents = amountCents,
    )
    val paidTotals = if (row.paymentConfigured && row.paymentLines.isNotEmpty()) {
        paymentLinesPaidTotalsLabel(row.paymentLines, rowAmountDefault.ifBlank { "0" })
    } else {
        null
    }
    val focusFirstName = remember { FocusRequester() }
    val focusLastName = remember { FocusRequester() }
    val focusEmail = remember { FocusRequester() }
    val focusPhone = remember { FocusRequester() }
    val focusDocId = remember { FocusRequester() }
    val focusAddress = remember { FocusRequester() }
    val focusAmount = remember { FocusRequester() }

    val linkedClient = remember(row.linkedClientId, existingClients) {
        row.linkedClientId?.let { id -> existingClients.firstOrNull { it.id == id } }
    }
    val personSearchQuery = remember(row.firstName, row.lastName, row.docId, row.email) {
        bulkEnrollPersonSearchQuery(row)
    }
    val existingHits = remember(existingClients, personSearchQuery, row.linkedClientId, excludeClientIds) {
        if (row.linkedClientId != null || personSearchQuery.length < 2) {
            emptyList()
        } else {
            filterClientsForPickerSearch(existingClients, personSearchQuery, limit = 6)
                .filter { it.id !in excludeClientIds }
        }
    }
    val docDuplicate = remember(row.docId, existingClients, row.linkedClientId) {
        val doc = row.docId.trim()
        if (doc.length < 3) null
        else existingClients.firstOrNull {
            it.docId?.equals(doc, ignoreCase = true) == true && it.id != row.linkedClientId
        }
    }

    // Misma lógica que órdenes / alta masiva pagos: aviso al elegir plan + fecha.
    LaunchedEffect(
        row.kind,
        row.planId,
        row.startsAt,
        row.linkedClientId,
        row.docId,
        row.email,
        row.firstName,
        row.lastName,
        siblingRows.map { "${it.id}:${it.planId}:${it.startsAt}:${it.linkedClientId}:${it.docId}:${it.email}" },
    ) {
        if (row.kind == BulkEnrollKind.RECORD_ONLY || row.planId.isNullOrBlank()) {
            if (row.periodConflictMsg != null) {
                onRowChange(row.copy(periodConflictMsg = null))
            }
            return@LaunchedEffect
        }
        val plan = plans.firstOrNull { it.id == row.planId } ?: return@LaunchedEffect
        val startsAt = parseFlexibleDateToIso(row.startsAt.trim())
            ?: row.startsAt.trim().ifBlank { bulkFormTodayIsoDate() }
        val periodKey = membershipPeriodKey(plan.billingPeriod, startsAt)
        val clientId = resolveBulkClientIdForPeriodCheck(row, existingClients)

        val localDup = periodKey != null && siblingRows.any { other ->
            if (other.id == row.id || other.kind == BulkEnrollKind.RECORD_ONLY) return@any false
            if (other.planId != row.planId) return@any false
            val otherPlan = plans.firstOrNull { it.id == other.planId } ?: return@any false
            val otherStart = parseFlexibleDateToIso(other.startsAt.trim())
                ?: other.startsAt.trim().ifBlank { bulkFormTodayIsoDate() }
            if (membershipPeriodKey(otherPlan.billingPeriod, otherStart) != periodKey) return@any false
            sameBulkPersonForPeriod(row, other, existingClients)
        }
        val apiDup = if (!localDup && !clientId.isNullOrBlank()) {
            runCatching {
                AdminRepo.api.isMembershipPeriodCovered(
                    clientId = clientId,
                    planId = row.planId!!,
                    orderDate = startsAt.take(10),
                )
            }.getOrDefault(false)
        } else {
            false
        }
        val msg = when {
            localDup -> duplicateMembershipPeriodMessage(plan.name, plan.billingPeriod) +
                " (repetido en esta planilla)."
            apiDup -> duplicateMembershipPeriodMessage(plan.name, plan.billingPeriod)
            else -> null
        }
        if (msg != row.periodConflictMsg) {
            onRowChange(row.copy(periodConflictMsg = msg))
        }
    }

    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.Surface)
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(16.dp))
            .padding(16.dp),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                "Persona ${index + 1}",
                fontWeight = FontWeight.Bold,
                fontSize = 15.sp,
                color = AdminColors.Text,
            )
            if (canRemove) {
                Text(
                    "Quitar",
                    color = AdminColors.Error,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.clickable(enabled = enabled, onClick = onRemove),
                )
            }
        }
        Spacer(Modifier.height(14.dp))
        // Escritorio: foto a la izquierda y datos a la derecha. Celular: foto arriba y datos debajo.
        BoxWithConstraints(Modifier.fillMaxWidth()) {
        val narrowCard = maxWidth < 600.dp
        BulkCardSplit(
            narrow = narrowCard,
            first = {
            BulkMemberPhotoBlock(
                firstName = row.firstName,
                lastName = row.lastName,
                photo = row.photo,
                fullWidth = narrowCard,
                enabled = enabled,
                onUpload = onUploadPhoto,
                onCapture = onCapturePhoto,
                onClear = onClearPhoto,
            )
            },
            second = {
            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Datos personales", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.Text)
                Text(
                    "Al escribir nombre o documento verás socios ya registrados para no duplicarlos.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                linkedClient?.let { client ->
                    BulkExistingClientBanner(
                        client = client,
                        enabled = enabled,
                        onClear = { onRowChange(row.clearLinkedClient()) },
                    )
                }
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    BulkCardField(
                        "Nombre *",
                        row.firstName,
                        enabled,
                        Modifier.weight(1f),
                        focusRequester = focusFirstName,
                        onNext = { focusLastName.requestFocus() },
                    ) {
                        onRowChange(row.editPerson { copy(firstName = it) })
                    }
                    BulkCardField(
                        "Apellido *",
                        row.lastName,
                        enabled,
                        Modifier.weight(1f),
                        focusRequester = focusLastName,
                        onNext = { focusEmail.requestFocus() },
                        onPrev = { focusFirstName.requestFocus() },
                    ) {
                        onRowChange(row.editPerson { copy(lastName = it) })
                    }
                }
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    BulkCardField(
                        "Correo",
                        row.email,
                        enabled,
                        Modifier.weight(1f),
                        focusRequester = focusEmail,
                        onNext = { focusPhone.requestFocus() },
                        onPrev = { focusLastName.requestFocus() },
                    ) {
                        onRowChange(row.editPerson { copy(email = it) })
                    }
                    BulkCardField(
                        "Teléfono",
                        row.phone,
                        enabled,
                        Modifier.weight(1f),
                        focusRequester = focusPhone,
                        onNext = { focusDocId.requestFocus() },
                        onPrev = { focusEmail.requestFocus() },
                    ) {
                        onRowChange(row.editPerson { copy(phone = it) })
                    }
                }
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    BulkCardField(
                        "Documento",
                        row.docId,
                        enabled,
                        Modifier.weight(1f),
                        focusRequester = focusDocId,
                        onNext = { focusAddress.requestFocus() },
                        onPrev = { focusPhone.requestFocus() },
                    ) {
                        onRowChange(row.editPerson { copy(docId = it) })
                    }
                    BulkCardSexField(row.sex, enabled, Modifier.weight(1f)) {
                        onRowChange(row.editPerson { copy(sex = it) })
                        focusAddress.requestFocus()
                    }
                }
                BulkCardField(
                    "Dirección",
                    row.address,
                    enabled,
                    Modifier.fillMaxWidth(),
                    placeholder = "Opcional",
                    focusRequester = focusAddress,
                    onNext = {
                        if (row.kind == BulkEnrollKind.MEMBERSHIP) focusAmount.requestFocus()
                    },
                    onPrev = { focusDocId.requestFocus() },
                ) {
                    onRowChange(row.editPerson { copy(address = it) })
                }
                if (docDuplicate != null && row.linkedClientId == null) {
                    Text(
                        "Ya hay un socio con ese documento: ${docDuplicate.fullName}. " +
                            "Selecciona la sugerencia para reutilizarlo.",
                        color = AdminColors.Error,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium,
                    )
                }
                if (existingHits.isNotEmpty()) {
                    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text(
                            "Socios ya registrados",
                            color = AdminColors.Primary,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                        existingHits.forEach { client ->
                            BulkExistingClientHit(
                                client = client,
                                enabled = enabled,
                                onSelect = { onRowChange(row.withExistingClient(client)) },
                            )
                        }
                    }
                } else if (personSearchQuery.length >= 2 && row.linkedClientId == null) {
                    Text(
                        "Ningún socio coincide con “$personSearchQuery”. Se creará uno nuevo al procesar.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
            }
            },
        )
        }

        Spacer(Modifier.height(16.dp))
        Text("Membresía y cobro", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.Text)
        Spacer(Modifier.height(10.dp))
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            BulkEnrollKind.entries.forEach { kind ->
                BulkEnrollKindChip(
                    label = kind.label,
                    selected = row.kind == kind,
                    enabled = enabled,
                    modifier = Modifier.weight(1f),
                    onClick = {
                        if (row.kind == kind) return@BulkEnrollKindChip
                        onRowChange(row.withKind(kind))
                    },
                )
            }
        }
        when (row.kind) {
            BulkEnrollKind.RECORD_ONLY -> {
                Spacer(Modifier.height(8.dp))
                Text(
                    "Solo se guarda la ficha. Sin plan ni cobro.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            BulkEnrollKind.MEMBERSHIP -> Unit
        }
        if (row.kind != BulkEnrollKind.RECORD_ONLY) {
        Spacer(Modifier.height(10.dp))
        val selectedPlan = plans.firstOrNull { it.id == row.planId }
        val startsIso = row.startsAt.ifBlank { bulkFormTodayIsoDate() }
        val endsIso = row.endsAt.ifBlank {
            selectedPlan?.let { planEndsAtFromStart(startsIso, it.billingPeriod) }.orEmpty()
        }
        // $ del plan de membresía (priceUsdCents), no conversión BCV.
        val usdDisplay = selectedPlan?.priceUsdCents?.takeIf { it > 0 }
            ?.let { formatMoney(it.toInt(), "USD") }
            .orEmpty()
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.Top,
        ) {
            BulkCardDropdown(
                label = "Plan",
                value = selectedPlan?.name ?: "Plan",
                enabled = enabled,
                options = plans.map { it.id to it.name },
                onSelect = { id ->
                    val plan = plans.firstOrNull { it.id == id }
                    val start = row.startsAt.ifBlank { bulkFormTodayIsoDate() }
                    onRowChange(
                        row.copy(
                            planId = id,
                            amountOverride = plan?.let { (it.priceCents / 100.0).toString() } ?: "",
                            endsAt = plan?.let { planEndsAtFromStart(start, it.billingPeriod) }.orEmpty(),
                            result = null,
                            ok = null,
                        ),
                    )
                },
                modifier = Modifier.weight(1f),
            )
            BulkCardDropdown(
                label = "Entrenador",
                value = trainers.firstOrNull { it.id == row.trainerId }?.displayName ?: "Sin entrenador",
                enabled = enabled,
                options = listOf(null to "Sin entrenador") + trainers.map { it.id to it.displayName },
                onSelect = { id -> onRowChange(row.copy(trainerId = id, result = null, ok = null)) },
                modifier = Modifier.weight(1f),
            )
        }
        Spacer(Modifier.height(10.dp))
        // Fila 2: pago, inicio y vencimiento en la misma fila (el vencimiento se recalcula con inicio y plan).
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.Top,
        ) {
            Column(Modifier.weight(0.85f)) {
                AdminDateField(
                    label = "Pago",
                    value = row.paidAt.ifBlank { bulkFormTodayIsoDate() },
                    onValueChange = { onRowChange(row.copy(paidAt = it, result = null, ok = null)) },
                    enabled = enabled,
                    placeholder = "AAAA-MM-DD",
                    outputFormat = AdminDateOutputFormat.ISO,
                )
                Text(
                    "Por defecto hoy; editable.",
                    color = AdminColors.TextMuted,
                    fontSize = 10.sp,
                    modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                )
            }
            Column(Modifier.weight(0.85f)) {
                AdminDateField(
                    label = "Inicio",
                    value = startsIso,
                    onValueChange = { newStart ->
                        val plan = plans.firstOrNull { it.id == row.planId }
                        val start = newStart.ifBlank { bulkFormTodayIsoDate() }
                        onRowChange(
                            row.copy(
                                startsAt = newStart,
                                endsAt = plan?.let { planEndsAtFromStart(start, it.billingPeriod) }.orEmpty(),
                                result = null,
                                ok = null,
                            ),
                        )
                    },
                    enabled = enabled,
                    placeholder = "AAAA-MM-DD",
                    outputFormat = AdminDateOutputFormat.ISO,
                )
                Text(
                    "Inicio de vigencia.",
                    color = AdminColors.TextMuted,
                    fontSize = 10.sp,
                    modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                )
            }
            Column(Modifier.weight(0.95f)) {
                AdminDateField(
                    label = "Fecha de Vencimiento",
                    value = endsIso,
                    onValueChange = { onRowChange(row.copy(endsAt = it, result = null, ok = null)) },
                    enabled = enabled,
                    placeholder = "AAAA-MM-DD",
                    outputFormat = AdminDateOutputFormat.ISO,
                )
                Text(
                    "Se recalcula con inicio/plan; editable.",
                    color = AdminColors.TextMuted,
                    fontSize = 10.sp,
                    modifier = Modifier.padding(top = 2.dp, start = 2.dp),
                )
            }
        }
        row.periodConflictMsg?.let { warn ->
            Spacer(Modifier.height(8.dp))
            Text(
                warn,
                color = AdminColors.Error,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
            )
        }
        if (row.kind == BulkEnrollKind.MEMBERSHIP) {
            Spacer(Modifier.height(10.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalAlignment = Alignment.Top,
            ) {
                BulkCardField(
                    label = "Monto (${GymlyCurrency.SYMBOL})",
                    value = row.amountOverride,
                    placeholder = rowAmountDefault.ifBlank { "0" },
                    enabled = enabled,
                    modifier = Modifier.weight(1f),
                    focusRequester = focusAmount,
                    onNext = onOpenPayment,
                    onPrev = { focusAddress.requestFocus() },
                    imeAction = ImeAction.Done,
                ) {
                    onRowChange(row.copy(amountOverride = it, result = null, ok = null))
                }
                BulkCardField(
                    label = "Monto ($)",
                    value = usdDisplay,
                    placeholder = "—",
                    enabled = false,
                    modifier = Modifier.weight(1f),
                ) { }
            }
            Text(
                "Bs. editable · $ del plan de membresía. Vencimiento según plan + inicio (editable).",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp, start = 4.dp),
            )
            Spacer(Modifier.height(12.dp))
            // Celular: la fila se apila (resumen arriba y botón abajo); en pantallas anchas queda en una fila.
            BoxWithConstraints(Modifier.fillMaxWidth()) {
            val stackedPay = maxWidth < 600.dp
            val cardMod = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(14.dp))
                .background(AdminColors.Surface)
                .border(1.dp, AdminColors.Primary.copy(alpha = 0.28f), RoundedCornerShape(14.dp))
                .padding(14.dp)
            val payInfo: @Composable (Modifier) -> Unit = { infoMod ->
            Column(infoMod) {
                Text("Pago", fontWeight = FontWeight.Bold, fontSize = 14.sp)
                Text(paymentSummary, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 2.dp))
                if (row.paymentConfigured && row.paymentStatus != org.gymly.model.AdminMembershipOrderStatus.COURTESY) {
                    val bsLabel = paidTotals ?: formatMoney(amountCents.toInt(), GymlyCurrency.CODE)
                    Text(
                        if (usdDisplay.isNotBlank()) "$bsLabel · $usdDisplay" else bsLabel,
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = AdminColors.Primary,
                        modifier = Modifier.padding(top = 4.dp),
                    )
                    // Desglose completo (métodos, vuelto, pendiente y descuento en Bs o $), igual que al cobrar.
                    BulkPaymentBreakdownList(
                        buildBulkPaymentBreakdown(
                            lines = row.paymentLines,
                            walletApplyCents = 0L,
                            discountBsCents = row.discount?.cents ?: 0L,
                            discountUsdCents = row.discount?.usdCents ?: 0L,
                            changeBsCents = row.changeGivenCents,
                            changeUsdCents = row.changeGivenUsdCents,
                            pendingBsCents = row.pendingBsCents,
                            pendingUsdCents = row.pendingUsdCents,
                            dueBsCents = bulkNetDueBsCents(amountCents, row.pendingBsCents, row.discount?.cents ?: 0L),
                            discountReason = row.discount?.reason,
                        ),
                    )
                }
            }
            }
            val payCell: @Composable () -> Unit = {
                BulkRowPaymentCell(
                    status = row.paymentStatus,
                    method = row.payMethod,
                    paymentConfigured = row.paymentConfigured,
                    enabled = enabled,
                    paymentSummary = if (row.paymentConfigured) paymentSummary else null,
                    onOpenPayment = onOpenPayment,
                    onClearPayment = onClearPayment,
                )
            }
            if (stackedPay) {
                Column(cardMod, verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    payInfo(Modifier.fillMaxWidth())
                    payCell()
                }
            } else {
                Row(
                    cardMod,
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    payInfo(Modifier.weight(1f).padding(end = 12.dp))
                    payCell()
                }
            }
        }
        }
        }

        row.result?.let { result ->
            Spacer(Modifier.height(8.dp))
            Text(
                result,
                color = when (row.ok) {
                    true -> AdminColors.Primary
                    false -> AdminColors.Error
                    null -> AdminColors.TextMuted
                },
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
            )
        }
    }
}

enum class BulkEnrollKind(val label: String) {
    MEMBERSHIP("Membresía"),
    RECORD_ONLY("Solo registro"),
}

fun BulkMemberRow.withKind(kind: BulkEnrollKind): BulkMemberRow = when (kind) {
    BulkEnrollKind.RECORD_ONLY -> copy(kind = kind, result = null, ok = null)
    BulkEnrollKind.MEMBERSHIP -> copy(
        kind = kind,
        paymentStatus = if (paymentLines.isNotEmpty() && paymentStatus != AdminMembershipOrderStatus.COURTESY) {
            paymentStatus
        } else {
            AdminMembershipOrderStatus.PENDING
        },
        paymentConfigured = paymentLines.isNotEmpty() && paymentStatus != AdminMembershipOrderStatus.COURTESY,
        result = null,
        ok = null,
    )
}

data class BulkMemberRow(
    val id: String,
    var kind: BulkEnrollKind = BulkEnrollKind.MEMBERSHIP,
    var firstName: String = "",
    var lastName: String = "",
    var email: String = "",
    var phone: String = "",
    var docId: String = "",
    var sex: AdminPersonSex? = null,
    var address: String = "",
    /** Si se eligió un socio existente, no se crea ficha nueva al procesar. */
    var linkedClientId: String? = null,
    var planId: String? = null,
    var trainerId: String? = null,
    /** Fecha de pago del cobro (yyyy-MM-dd); por defecto hoy. */
    var paidAt: String = "",
    var startsAt: String = "",
    /** Vencimiento editable; se recalcula al cambiar inicio o plan. */
    var endsAt: String = "",
    var amountOverride: String = "",
    var paymentStatus: org.gymly.model.AdminMembershipOrderStatus = org.gymly.model.AdminMembershipOrderStatus.PENDING,
    var payMethod: org.gymly.model.AdminPaymentMethod? = null,
    var payerPhone: String = "",
    var paymentConfigured: Boolean = false,
    var paymentLines: List<PaymentLineDraft> = emptyList(),
    var changeGivenCents: Long = 0L,
    var changeGivenUsdCents: Long = 0L,
    /** Pendiente en USD al confirmar cobro; 0 = sin deuda \$. */
    var pendingUsdCents: Long = 0L,
    /** Pendiente en Bs al confirmar cobro; 0 = sin deuda Bs. */
    var pendingBsCents: Long = 0L,
    /** Descuento en Bs de esta persona (solo al cobrar la orden creada); null = sin descuento. */
    var discount: AdminPaymentDiscount? = null,
    var photo: PickedImagePayload? = null,
    var photoName: String? = null,
    var result: String? = null,
    /** Orden ya creada en un intento anterior (cobro no aplicado): al corregir solo se reintenta el cobro. */
    var createdOrderId: String? = null,
    var createdOrderLabel: String? = null,
    var ok: Boolean? = null,
    /** Aviso en vivo: mismo plan + periodo ya registrado (API o planilla). */
    var periodConflictMsg: String? = null,
)

/** Cliente existente resoluble para chequear plan+periodo (sin crear ficha). */
internal fun resolveBulkClientIdForPeriodCheck(
    row: BulkMemberRow,
    existing: List<AdminClient>,
): String? {
    row.linkedClientId?.let { return it }
    val email = row.email.trim()
    if (email.isNotEmpty()) {
        existing.firstOrNull { it.email?.trim()?.equals(email, ignoreCase = true) == true }?.id?.let { return it }
    }
    val doc = row.docId.trim()
    if (doc.isNotEmpty()) {
        existing.firstOrNull { it.docId?.trim()?.equals(doc, ignoreCase = true) == true }?.id?.let { return it }
    }
    return null
}

/** Misma persona en la planilla (vinculado, cédula o correo). */
internal fun sameBulkPersonForPeriod(
    a: BulkMemberRow,
    b: BulkMemberRow,
    existing: List<AdminClient>,
): Boolean {
    val aId = resolveBulkClientIdForPeriodCheck(a, existing)
    val bId = resolveBulkClientIdForPeriodCheck(b, existing)
    if (aId != null && bId != null && aId == bId) return true
    val docA = a.docId.trim()
    val docB = b.docId.trim()
    if (docA.length >= 3 && docA.equals(docB, ignoreCase = true)) return true
    val emailA = a.email.trim()
    val emailB = b.email.trim()
    if (emailA.isNotEmpty() && emailA.equals(emailB, ignoreCase = true)) return true
    val nameA = "${a.firstName.trim()} ${a.lastName.trim()}".trim().lowercase()
    val nameB = "${b.firstName.trim()} ${b.lastName.trim()}".trim().lowercase()
    return nameA.length >= 3 && nameA == nameB
}

private fun bulkEnrollPersonSearchQuery(row: BulkMemberRow): String {
    val doc = row.docId.trim()
    if (doc.length >= 2) return doc
    val fullName = "${row.firstName.trim()} ${row.lastName.trim()}".trim()
    if (fullName.length >= 2) return fullName
    val first = row.firstName.trim()
    if (first.length >= 2) return first
    val email = row.email.trim()
    if (email.length >= 2) return email
    return ""
}

@Composable
private fun BulkExistingClientBanner(
    client: AdminClient,
    enabled: Boolean,
    onClear: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(AdminColors.Primary.copy(alpha = 0.1f))
            .border(1.dp, AdminColors.Primary.copy(alpha = 0.35f), RoundedCornerShape(10.dp))
            .padding(horizontal = 10.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                "Socio existente: ${client.fullName}",
                color = AdminColors.Primary,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Text(
                listOfNotNull(client.docId?.let { "Doc. $it" }, client.email).joinToString(" · ")
                    .ifBlank { "Se reutilizará al procesar (no se crea ficha nueva)." },
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
        Text(
            "Soltar",
            color = AdminColors.Error,
            fontSize = 12.sp,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.clickable(enabled = enabled, onClick = onClear),
        )
    }
}

@Composable
private fun BulkExistingClientHit(
    client: AdminClient,
    enabled: Boolean,
    onSelect: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.7f), RoundedCornerShape(10.dp))
            .background(AdminColors.SurfaceContainer)
            .clickable(enabled = enabled, onClick = onSelect)
            .padding(horizontal = 10.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        BulkClientPhotoPreview(client = client, size = 40.dp)
        Column(Modifier.weight(1f)) {
            Text(client.fullName, fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.Text)
            Text(
                listOfNotNull(client.docId?.let { "Doc. $it" }, client.email, client.membershipPlanName)
                    .joinToString(" · ")
                    .ifBlank { "Usar este socio" },
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
        Text("Usar", color = AdminColors.Primary, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
    }
}

@Composable
private fun BulkEnrollKindChip(
    label: String,
    selected: Boolean,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    val bg = if (selected) AdminColors.Primary else AdminColors.Outline.copy(alpha = 0.35f)
    val fg = if (selected) AdminColors.OnBrand else AdminColors.TextMuted
    Text(
        label,
        modifier = modifier
            .clip(RoundedCornerShape(20.dp))
            .background(bg)
            .clickable(enabled = enabled, onClick = onClick)
            .padding(horizontal = 8.dp, vertical = 8.dp),
        color = fg,
        fontWeight = FontWeight.SemiBold,
        fontSize = 13.sp,
        textAlign = TextAlign.Center,
        maxLines = 1,
    )
}

@Composable
private fun BulkCardField(
    label: String,
    value: String,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    placeholder: String = "",
    focusRequester: FocusRequester? = null,
    onNext: (() -> Unit)? = null,
    onPrev: (() -> Unit)? = null,
    imeAction: ImeAction = ImeAction.Next,
    onChange: (String) -> Unit,
) {
    fun goNext() {
        onNext?.invoke()
    }
    fun goPrev() {
        onPrev?.invoke()
    }
    Column(modifier) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp, fontWeight = FontWeight.Medium)
        Spacer(Modifier.height(4.dp))
        OutlinedTextField(
            value = value,
            onValueChange = onChange,
            enabled = enabled,
            singleLine = true,
            placeholder = if (placeholder.isNotBlank()) {
                { Text(placeholder, color = AdminColors.TextMuted, fontSize = 12.sp) }
            } else {
                null
            },
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 48.dp)
                .then(if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier)
                .onPreviewKeyEvent { event ->
                    if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                    when (event.key) {
                        Key.Enter, Key.NumPadEnter -> {
                            goNext()
                            true
                        }
                        Key.Tab -> {
                            if (event.isShiftPressed) goPrev() else goNext()
                            true
                        }
                        Key.DirectionRight, Key.DirectionDown -> {
                            goNext()
                            true
                        }
                        Key.DirectionLeft, Key.DirectionUp -> {
                            goPrev()
                            true
                        }
                        else -> false
                    }
                },
            shape = RoundedCornerShape(10.dp),
            keyboardOptions = KeyboardOptions(imeAction = imeAction),
            keyboardActions = KeyboardActions(
                onNext = { goNext() },
                onDone = { goNext() },
            ),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = AdminColors.Primary,
                unfocusedBorderColor = AdminColors.Outline,
            ),
        )
    }
}

@Composable
private fun BulkCardSexField(
    value: AdminPersonSex?,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    onChange: (AdminPersonSex?) -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    Column(modifier) {
        Text("Sexo", color = AdminColors.TextMuted, fontSize = 12.sp, fontWeight = FontWeight.Medium)
        Spacer(Modifier.height(4.dp))
        Box {
            OutlinedButton(
                onClick = { if (enabled) open = true },
                enabled = enabled,
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth().heightIn(min = 48.dp),
            ) {
                Text(value?.labelEs() ?: "Opcional", color = AdminColors.Text, fontSize = 13.sp)
            }
            DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
                DropdownMenuItem(
                    text = { Text("Sin indicar") },
                    onClick = {
                        onChange(null)
                        open = false
                    },
                )
                AdminPersonSex.entries.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.labelEs()) },
                        onClick = {
                            onChange(option)
                            open = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun BulkCardDropdown(
    label: String,
    value: String,
    enabled: Boolean,
    options: List<Pair<String?, String>>,
    onSelect: (String?) -> Unit,
    modifier: Modifier = Modifier,
) {
    var open by remember { mutableStateOf(false) }
    Column(modifier) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp, fontWeight = FontWeight.Medium)
        Spacer(Modifier.height(4.dp))
        Box {
            OutlinedButton(
                onClick = { if (enabled) open = true },
                enabled = enabled,
                shape = RoundedCornerShape(10.dp),
                modifier = Modifier.fillMaxWidth().heightIn(min = 48.dp),
            ) {
                Text(value, color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
            }
            DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
                options.forEach { (id, text) ->
                    DropdownMenuItem(
                        text = { Text(text) },
                        onClick = {
                            onSelect(id)
                            open = false
                        },
                    )
                }
            }
        }
    }
}

internal fun bulkFormTodayIsoDate(): String =
    todayIsoDate().ifBlank { "2026-01-01" }

/** Instant ISO para collectPayments.paidAt a partir de yyyy-MM-dd. */
internal fun paymentDateToPaidAtIso(dateRaw: String): String {
    val day = parseFlexibleDateToIso(dateRaw)?.take(10)
        ?: dateRaw.take(10).takeIf { it.length >= 10 }
        ?: bulkFormTodayIsoDate()
    return "${day}T12:00:00Z"
}

/**
 * Clave de periodo alineada con backend ([ClientAccountService.periodKey]):
 * día / semana ISO / mes / año. Sirve para detectar duplicados en cola local.
 */
/**
 * Misma semántica que backend [ClientAccountService.periodKey]:
 * WEEKLY usa año calendario + IsoFields.WEEK_OF_WEEK_BASED_YEAR.
 */
internal fun membershipPeriodKey(period: AdminBillingPeriod, startsAtIso: String): String? {
    val iso = parseFlexibleDateToIso(startsAtIso)?.take(10) ?: return null
    val parts = iso.split("-").mapNotNull { it.toIntOrNull() }
    if (parts.size != 3) return null
    val y = parts[0]
    val m = parts[1]
    return when (period) {
        AdminBillingPeriod.DAILY -> "D:$iso"
        AdminBillingPeriod.WEEKLY -> {
            val epochDay = runCatching {
                Instant.parse("${iso}T00:00:00Z").epochSeconds / 86_400L
            }.getOrNull() ?: return null
            // ISO: lunes=0 … domingo=6 (1970-01-01 = jueves → +3).
            val dayOfWeek = ((epochDay + 3) % 7).let { if (it < 0) it + 7 else it }
            val monday = epochDay - dayOfWeek
            val thursday = monday + 3
            val thuIso = Instant.fromEpochSeconds(thursday * 86_400L).toString().take(10)
            val weekBasedYear = thuIso.substring(0, 4).toIntOrNull() ?: y
            val jan4 = Instant.parse("$weekBasedYear-01-04T00:00:00Z").epochSeconds / 86_400L
            val jan4Dow = ((jan4 + 3) % 7).let { if (it < 0) it + 7 else it }
            val week1Monday = jan4 - jan4Dow
            val week = ((monday - week1Monday) / 7 + 1).toInt().coerceIn(1, 53)
            // Backend: date.getYear() + WEEK_OF_WEEK_BASED_YEAR (no week-based year).
            "W:$y-$week"
        }
        AdminBillingPeriod.MONTHLY -> "M:$y-$m"
        AdminBillingPeriod.ANNUAL -> "Y:$y"
        AdminBillingPeriod.COURTESY -> "C:$iso"
    }
}

/** Mensaje visible cuando ya existe cobro/orden del mismo plan en el mismo periodo. */
internal fun duplicateMembershipPeriodMessage(planName: String, period: AdminBillingPeriod): String {
    val periodWord = when (period) {
        AdminBillingPeriod.DAILY -> "día"
        AdminBillingPeriod.WEEKLY -> "semana"
        AdminBillingPeriod.MONTHLY -> "mes"
        AdminBillingPeriod.ANNUAL -> "año"
        AdminBillingPeriod.COURTESY -> "cortesía"
    }
    return "Plan/periodo duplicado: «$planName» ya está registrado para este $periodWord. " +
        "No se puede pagar el mismo plan dos veces en el mismo periodo."
}

/** Fin de vigencia = inicio + periodo del plan (igual que backend defaultEndsAt). */
internal fun planEndsAtFromStart(startsAtIso: String, period: AdminBillingPeriod): String? {
    val iso = parseFlexibleDateToIso(startsAtIso) ?: return null
    val parts = iso.split("-").mapNotNull { it.toIntOrNull() }
    if (parts.size != 3) return null
    val y = parts[0]
    val m = parts[1]
    val d = parts[2]
    return when (period) {
        AdminBillingPeriod.DAILY -> addDaysToIsoDate(iso, 1)
        AdminBillingPeriod.WEEKLY -> addDaysToIsoDate(iso, 7)
        AdminBillingPeriod.MONTHLY -> addCalendarMonthsToIsoDate(y, m, d, 1)
        AdminBillingPeriod.ANNUAL -> addCalendarMonthsToIsoDate(y, m, d, 12)
        // Cortesía: el vencimiento se escribe a mano; no se calcula desde el plan.
        AdminBillingPeriod.COURTESY -> null
    }
}

private fun addDaysToIsoDate(iso: String, days: Int): String? =
    runCatching {
        val epochDay = Instant.parse("${iso.take(10)}T00:00:00Z").epochSeconds / 86_400L
        Instant.fromEpochSeconds((epochDay + days) * 86_400L).toString().take(10)
    }.getOrNull()

private fun addCalendarMonthsToIsoDate(year: Int, month: Int, day: Int, monthsToAdd: Int): String {
    val total = year * 12 + (month - 1) + monthsToAdd
    val ny = total / 12
    val nm = total % 12 + 1
    val maxDay = daysInMonth(ny, nm)
    val nd = day.coerceAtMost(maxDay)
    return "$ny-${nm.toString().padStart(2, '0')}-${nd.toString().padStart(2, '0')}"
}

private fun daysInMonth(year: Int, month: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    2 -> if (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) 29 else 28
    else -> 30
}
